import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { GuideCardGrid } from "@/components/guide/GuideSections";
import { Reveal } from "@/components/Reveal";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqAccordion } from "@/components/FaqAccordion";
import { JsonLd } from "@/components/JsonLd";
import { ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { INDUSTRIES } from "@/content/industries";
import { SITE } from "@/content/site";
import { RelatedReading } from "@/components/RelatedReading";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import { faqSchema } from "@/lib/schema";

/**
 * Per-industry deep-dive playbooks rendered above the closing CTA.
 * Hrefs must resolve through `src/content/internal-links.ts`.
 */
const INDUSTRY_PLAYBOOKS: Record<string, string[]> = {
  saas: [
    "/blog/seo-b2b-saas-founders-first-50-pages",
    "/blog/aeo-for-saas-complete-playbook-2026",
    "/blog/seo-for-dhaka-saas-startups-2026",
    "/blog/saas-cited-47-chatgpt-answers-case-study",
    "/blog/schema-markup-adoption-saas-benchmark-2026",
  ],
  ecommerce: [
    "/blog/aeo-for-ecommerce-product-schema-playbook",
    "/blog/seo-shopify-woocommerce-migration-safe-2026",
    "/blog/chattogram-ecommerce-seo-guide-2026",
    "/blog/pagination-faceted-nav-canonical-crawl-traps",
  ],
  "law-firms": [
    "/blog/aeo-for-law-firms-ymyl-trust-playbook",
    "/blog/ai-overviews-ymyl-citation-audit",
    "/blog/entity-seo-signals-for-aeo",
  ],
  healthcare: [
    "/blog/seo-healthcare-clinics-ymyl-bd",
    "/blog/ai-overviews-ymyl-citation-audit",
    "/blog/google-business-profile-optimization-checklist-2026",
    "/blog/review-generation-google-filter-compliant",
  ],
  "real-estate": [
    "/blog/seo-real-estate-portals-listing-schema",
    "/blog/programmatic-aeo-at-scale-1000-pages",
    "/blog/local-seo-service-area-businesses-no-storefront",
  ],
  restaurants: [
    "/blog/seo-restaurants-menu-schema-local-pack",
    "/blog/google-business-profile-optimization-checklist-2026",
    "/blog/review-generation-google-filter-compliant",
    "/blog/local-citation-building-bangladesh-27-directories",
  ],
};

/** Bridge each industry to its strongest matching city page. */
const INDUSTRY_CITY: Record<string, { slug: string; label: string }> = {
  saas: { slug: "dhaka", label: "SEO services in Dhaka" },
  ecommerce: { slug: "chittagong", label: "SEO services in Chattogram" },
  restaurants: { slug: "sylhet", label: "SEO services in Sylhet" },
  healthcare: { slug: "rangpur", label: "SEO services in Rangpur" },
};

export const Route = createFileRoute("/seo-for/$industry")({
  head: ({ params }) => {
    const ind = INDUSTRIES.find((i) => i.slug === params.industry);
    if (!ind) {
      return buildSeo({
        title: "Industry not found | Freelancer Tamal",
        description: "Industry SEO playbook not found.",
        path: `/seo-for/${params.industry}`,
        noindex: true,
      });
    }
    return buildSeo({
      title: `SEO for ${ind.name} — Industry Playbook | Freelancer Tamal`,
      description: ind.intro,
      path: `/seo-for/${ind.slug}`,
      ogEyebrow: `SEO for ${ind.name}`,
      ogTitle: `SEO for ${ind.name}`,
      keywords: ind.keywords.join(", "),
    });
  },
  component: IndustryPage,
  notFoundComponent: () => (
    <Section eyebrow="404" title="Industry not found">
      <Link to="/seo-for" className="text-primary underline">Back to all industries</Link>
    </Section>
  ),
});

function IndustryPage() {
  const { industry } = Route.useParams();
  const ind = INDUSTRIES.find((i) => i.slug === industry);
  if (!ind) throw notFound();

  return (
    <>
      <Breadcrumbs items={[
        { name: "Home", path: "/" },
        { name: "SEO by Industry", path: "/seo-for" },
        { name: ind.name, path: `/seo-for/${ind.slug}` },
      ]} />

      <Section headingLevel={1} eyebrow={`SEO for ${ind.name}`} title={<>{ind.hero}</>} subtitle={ind.intro}>
        <div className="flex flex-wrap gap-3 -mt-4">
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:shadow-[var(--shadow-glow)]">
            Book a strategy call <ArrowUpRight size={16} />
          </Link>
          <a href="https://tool.freelancertamal.com/" target="_blank" rel="noopener" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">
            Run free audit <ArrowUpRight size={16} />
          </a>
        </div>
      </Section>

      <div className="container-x pb-16">
        <Reveal>
          <div className="rounded-3xl border border-border bg-card p-8 lg:p-10">
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Common pain points</div>
            <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3">
              {ind.pain.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm">
                  <span className="mt-1 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-destructive/15 text-destructive text-xs font-bold">×</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <Section eyebrow="Approach" title={<>How I run <span className="text-gradient">{ind.name} SEO.</span></>}>
        <GuideCardGrid items={ind.approach.map((a) => ({ t: a.title, d: a.body }))} reveal check />
      </Section>

      {ind.faqs.length > 0 && (
        <Section eyebrow="FAQ" title={<>{ind.name} SEO <span className="text-gradient">questions.</span></>}>
          <FaqAccordion items={ind.faqs} />
        </Section>
      )}

      <Section className="!pt-0">
        <AiAnswersCallout
          context={`${ind.name} SEO`}
          questionSlugs={
            ind.slug === "ecommerce"
              ? [
                  "what-schema-for-aeo",
                  "get-cited-by-chatgpt",
                  "measure-aeo-citations",
                  "core-web-vitals-2026",
                ]
              : ind.slug === "saas"
                ? [
                    "aeo-worth-it-small-business",
                    "get-cited-by-chatgpt",
                    "measure-aeo-citations",
                    "what-is-entity-seo",
                  ]
                : [
                    "local-seo-still-matter",
                    "aeo-worth-it-small-business",
                    "faqpage-schema-worth-it",
                    "what-is-aeo",
                  ]
          }
        />
      </Section>

      {INDUSTRY_PLAYBOOKS[ind.slug] && (
        <RelatedReading
          eyebrow={`${ind.name} playbooks`}
          title={<>Deep-dive guides for <span className="text-gradient">{ind.name.toLowerCase()}.</span></>}
          description="The same research and playbooks I use when leading engagements in this vertical."
          hrefs={[...INDUSTRY_PLAYBOOKS[ind.slug], "/services/aeo-geo"]}
        />
      )}

      {INDUSTRY_CITY[ind.slug] && (
        <Section className="!pt-0">
          <div className="rounded-3xl border border-border bg-card p-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Local intent</div>
              <p className="text-sm text-muted-foreground max-w-xl">
                Most of my {ind.name.toLowerCase()} clients also need hyperlocal coverage. Pair this playbook with the matching city page.
              </p>
            </div>
            <Link
              to="/seo-services/$location"
              params={{ location: INDUSTRY_CITY[ind.slug].slug }}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium"
            >
              {INDUSTRY_CITY[ind.slug].label} <ArrowUpRight size={14} />
            </Link>
          </div>
        </Section>
      )}

      <Section title={<>Ready to scale {ind.name.toLowerCase()} SEO?</>}>
        <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
          Start an engagement <ArrowUpRight size={16} />
        </Link>
        <div className="mt-10 grid gap-3 md:grid-cols-3">
          {INDUSTRIES.filter((i) => i.slug !== ind.slug).slice(0, 3).map((other) => (
            <Link key={other.slug} to="/seo-for/$industry" params={{ industry: other.slug }} className="rounded-2xl border border-border bg-card p-5 hover:border-primary/50 text-sm">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">{other.name}</div>
              <div className="text-foreground">{other.hero}</div>
            </Link>
          ))}
        </div>
      </Section>

      <JsonLd data={breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "SEO by Industry", path: "/seo-for" },
        { name: ind.name, path: `/seo-for/${ind.slug}` },
      ])} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Service",
        name: `SEO for ${ind.name}`,
        description: ind.intro,
        provider: { "@type": "Person", name: SITE.fullName, url: SITE.url },
        areaServed: "Worldwide",
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: `How I run ${ind.name} SEO`,
        description: ind.intro,
        step: ind.approach.map((a, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: a.title,
          text: a.body,
        })),
      }} />
      {ind.faqs.length > 0 && (
        <JsonLd data={faqSchema(ind.faqs)} />
      )}
    </>
  );
}